import type { GeoCoords, MapLocationDto } from '@/core/data/location/location.interface';
import type { GetCoordinates, GetTableParams, LoadItemsData, Result } from '@/core/network/http/httpClient.interface';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import debounce from 'lodash/debounce';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useIntersectStore } from './intersect.store';

interface ItemWithId {
  id: string;
}

export let incomingMarkers: MapLocationDto[] = [];
export let remainingMarkers: MapLocationDto[] = [];
export let outgoingMarkers: MapLocationDto[] = [];
export let updatedMarkers: MapLocationDto[] = [];
export let currentMarkers: MapLocationDto[] = [];
export const markerVersion = ref(0);

export const useMarkerStore = (listId: GamListId) =>
  defineStore(`${GamStoreId.MAP}-${listId}`, () => {
    const intersectStore = useIntersectStore();
    const isLoading = ref<boolean>(false);

    const lastUpdated = ref<number>(new Date().getTime());
    const selectedItem = ref<MapLocationDto | null>(null);
    const tableParams = ref<GetTableParams>({});
    const coordinates = ref<GetCoordinates>();
    const loadCallback = ref<((listId: GamListId, center?: GeoCoords) => Promise<Result<MapLocationDto[]>>) | null>(
      null,
    );

    const setLoadCallback = (callback: (listId: GamListId) => Promise<Result<MapLocationDto[]>>) => {
      loadCallback.value = callback;
    };

    const setLoadingState = (state: boolean) => {
      isLoading.value = state;
    };

    const setSearchQuery = async (data?: LoadItemsData): Promise<void> => {
      tableParams.value.search = data?.search;
      if (tableParams.value.search !== undefined && !data?.preventLoad) {
        await loadItems(data);
      }
    };

    const setLocationParam = (location: GeoCoords): void => {
      if (location) {
        coordinates.value = {
          latitude: location.lat || undefined,
          longitude: location.lng || undefined,
        };
        // } else {
        //   const { userLocation } = storeToRefs(useGeolocationStore());
        //   coordinates.value = {
        //     latitude: userLocation.value?.location.lat,
        //     longitude: userLocation.value?.location.lng,
        //   };
      }
    };

    const getLocationParam = (): GetCoordinates | undefined => {
      return coordinates.value;
    };

    const populateList = (items: MapLocationDto[]): void => {
      if (tableParams.value.afterCursor) {
        currentMarkers = currentMarkers.concat(items);
      } else {
        currentMarkers = items;
      }
      markerVersion.value++;
    };

    const setList = async (
      center: GeoCoords,
      resetListAfterLoad?: boolean,
      selectFirstItem?: boolean,
    ): Promise<void> => {
      setLoadingState(true);
      // console.log('setLoadingState #1 true');

      const result = await loadCallback.value?.(listId, center);

      if (result?.data) {
        if (resetListAfterLoad) {
          // resetList();
        }

        populateList(result.data);

        if (!tableParams.value.afterCursor && selectFirstItem) {
          // setSelectedItem(result.data[0]);
        }
      }

      setLoaded();
      setLoadingState(false);
      // console.log('setLoadingState #2 false');
    };

    const setSelectedItem = (item: MapLocationDto | null): void => {
      selectedItem.value = item;
    };

    const loadItems = async (data?: LoadItemsData): Promise<void> => {
      if (!data?.center) return;
      if (!data?.loadOnInit) resetList(data?.resetSelected);
      await setList(data.center, data?.loadOnInit, data?.selectFirstItem);
    };

    const replaceItems = async (items: MapLocationDto[]): Promise<void> => {
      currentMarkers = items;
      markerVersion.value++;

      setLoaded();
      setLoadingState(false);
      // console.log('setLoadingState #3 false');
    };

    const seedItems = (items: MapLocationDto[]): void => {
      currentMarkers = items;
      markerVersion.value++;
      incomingMarkers = items;
      lastUpdated.value = new Date().getTime();
      setLoaded();
      setLoadingState(false);
    };

    const addUniqueItems = debounce(
      async (center: GeoCoords): Promise<void> => {
        setLoadingState(true);
        // console.log('setLoadingState #4 true');

        const results = await loadCallback.value?.(listId, center);
        if (!results?.data?.length) {
          setLoaded();
          setLoadingState(false);
          // console.log('setLoadingState #5 false');
          return;
        }

        if (currentMarkers.length === 0) {
          currentMarkers = results.data || [];
          markerVersion.value++;
          incomingMarkers = results.data || [];
          lastUpdated.value = new Date().getTime();
          setLoaded();
          setLoadingState(false);
          // console.log('setLoadingState #6 false');
          return;
        }

        const freshById = new Map<string, MapLocationDto>();
        results.data?.forEach((item) => freshById.set(item.id, item));

        incomingMarkers = [];
        results.data?.forEach((newItem) => {
          if (currentMarkers.findIndex((oldItem) => newItem.id === (oldItem as ItemWithId).id) === -1) {
            incomingMarkers.push(newItem);
          }
        });

        outgoingMarkers = [];
        remainingMarkers = [];
        updatedMarkers = [];
        currentMarkers.forEach((oldItem) => {
          const freshItem = freshById.get((oldItem as ItemWithId).id);
          if (!freshItem) {
            outgoingMarkers.push(oldItem);
          } else {
            const gambitsChanged =
              (oldItem.nextGambits?.length ?? 0) !== (freshItem.nextGambits?.length ?? 0) ||
              oldItem.nextGambits?.[0]?.id !== freshItem.nextGambits?.[0]?.id;
            if (oldItem.visitorCount !== freshItem.visitorCount || gambitsChanged) {
              Object.assign(oldItem, freshItem);
              updatedMarkers.push(oldItem);
            }
            remainingMarkers.push(oldItem);
          }
        });

        currentMarkers = [...remainingMarkers, ...incomingMarkers];
        markerVersion.value++;

        if (outgoingMarkers.length || incomingMarkers.length || updatedMarkers.length) {
          lastUpdated.value = new Date().getTime();
        }

        setLoaded();
        setLoadingState(false);
        // console.log('setLoadingState #7 false');
      },
      128,
      { trailing: true },
    );

    const resetList = (resetSelected?: boolean): void => {
      populateList([]);
      if (resetSelected) setSelectedItem(null);
    };

    const setLoaded = () => {
      intersectStore.setLoadState(listId, true);
    };

    const updateItem = (update: Partial<MapLocationDto>): MapLocationDto | undefined => {
      const item = currentMarkers.find((item) => item.id === update.id);
      if (!item) {
        return;
      }

      Object.entries(update).forEach(([key, value]) => {
        // @ts-ignore
        item[key as keyof MapLocationDto] = value;
      });

      return item;
    };

    const addItem = (item: MapLocationDto) => {
      currentMarkers.push(item);
      markerVersion.value++;
    };

    const removeItem = (index: number) => {
      currentMarkers.splice(index, 1);
      markerVersion.value++;
    };

    return {
      isLoading,
      lastUpdated,
      selectedItem,
      tableParams,
      setLoadingState,
      setSearchQuery,
      setList,
      setSelectedItem,
      resetList,
      replaceItems,
      seedItems,
      loadItems,
      setLoadCallback,
      setLocationParam,
      getLocationParam,
      setLoaded,
      addUniqueItems,
      addItem,
      removeItem,
      updateItem,
    };
  });
